import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState, useCallback, useEffect, useRef, } from 'react';
import { useModal } from './useModal';
import { ModalDispatchContext, ModalRegistrationContext, ModalStateContext, } from './ModalContext';
import { _console } from '@shared/helpers';
const isModalDefinition = (modal) => {
    return typeof modal === 'object' && modal !== null && 'component' in modal;
};
const normalizeModalMap = (modals, onLegacyEntry) => {
    return Object.keys(modals).reduce((acc, key) => {
        const modalEntry = modals[key];
        if (isModalDefinition(modalEntry)) {
            acc[key] = modalEntry;
            return acc;
        }
        onLegacyEntry === null || onLegacyEntry === void 0 ? void 0 : onLegacyEntry(key);
        acc[key] = { component: modalEntry };
        return acc;
    }, {});
};
const ModalOrchestrator = ({ modals: initialModals, children, onDispatchReady, }) => {
    const warnedLegacyKeysRef = useRef(new Set());
    const warnLegacyEntry = useCallback((modalKey) => {
        if (process.env.NODE_ENV === 'production') {
            return;
        }
        if (warnedLegacyKeysRef.current.has(modalKey)) {
            return;
        }
        warnedLegacyKeysRef.current.add(modalKey);
        _console.warn(`[ModalOrchestrator] Modal key "${modalKey}" uses legacy registration shape. \n\n` +
            'Once legacy modal type is removed, your modal will not open.\n\n' +
            'Please migrate to { component, defaultProps? }.');
    }, []);
    const [modals, setModals] = useState(() => normalizeModalMap(initialModals, warnLegacyEntry));
    const registerModals = useCallback((namespace, newModals) => {
        const normalizedModals = normalizeModalMap(newModals, (modalKey) => warnLegacyEntry(`${namespace}/${modalKey}`));
        const namespacedModals = Object.keys(normalizedModals).reduce((acc, key) => {
            acc[`${namespace}/${key}`] = normalizedModals[key];
            return acc;
        }, {});
        setModals((prevModals) => (Object.assign(Object.assign({}, prevModals), namespacedModals)));
    }, [warnLegacyEntry]);
    const unregisterModals = useCallback((namespace) => {
        setModals((prevModals) => {
            const nextModals = Object.assign({}, prevModals);
            Object.keys(nextModals).forEach((key) => {
                if (key.startsWith(`${namespace}/`)) {
                    delete nextModals[key];
                }
            });
            return nextModals;
        });
    }, []);
    const { modal, ModalComponent, openModal, closeModal } = useModal(modals);
    const state = useMemo(() => ({ modal, ModalComponent }), [modal, ModalComponent]);
    const dispatch = useMemo(() => ({ openModal, closeModal }), [openModal, closeModal]);
    const registration = useMemo(() => ({ registerModals, unregisterModals }), [registerModals, unregisterModals]);
    useEffect(() => {
        onDispatchReady === null || onDispatchReady === void 0 ? void 0 : onDispatchReady(dispatch);
    }, [dispatch, onDispatchReady]);
    return (_jsx(ModalRegistrationContext.Provider, { value: registration, children: _jsx(ModalStateContext.Provider, { value: state, children: _jsx(ModalDispatchContext.Provider, { value: dispatch, children: children }) }) }));
};
export default ModalOrchestrator;
